import {
  EncodeStatusEnum,
  IAudio,
  IOverlay,
  LiveStreamEndOptionEnum,
  LiveStreamJingleEveryTypeEnum,
  LiveStreamOrientationEnum,
  LiveStreamPlaybackOrderEnum,
  LiveStreamResolutionEnum,
  LiveStreamStartOptionEnum,
} from "./models";

export interface IStreamingPlatformKey {
  id: string;
  platform: string;
  name: string;
  streamKey?: string;
  streamUrl?: string;
  channelId?: string;
}
export interface IPendingUploadItem {
  intentId?: string;
  releasePending?: boolean;
  releaseAfter?: string | null;
  id: string;
  streamId: string;
  body: File;
  uploaded: boolean;
  failed?: boolean;
  progress: number;
  folderId?: string;
  status?: "pending" | "uploading" | "completed" | "failed" | "cancelled";
  controller?: AbortController;
  totalBytes?: number;
  uploadedBytes?: number;
}
export interface IStreamSelectedItem {
  id: string;
  name: string;
  author?: string | null;
  duration: number;
  width?: number;
  height?: number;
  sizeInKB: number;
  type: "audio" | "video";
  url?: string;
  thubmnailUrl?: string | null;
  resolutionTier?: LiveStreamResolutionEnum | null;
  resolutionError?: string | null;
  thumbnailError?: string | null;
  liveStreamId?: string;
  encodeStatus?: EncodeStatusEnum;
  encodeError?: string | null;
  order: number;
  isChecked?: boolean;
}

export interface ILiveStreamScheduledShow {
  name: string;
  /** Minutes after local midnight (0–1439); the show repeats daily (decision #13). */
  startMinuteOfDay: number;
  /** IANA time zone of startMinuteOfDay, e.g. "Europe/Berlin". */
  timezone: string;
  playlistId?: string;
  videoId?: string;
  switchMode: "wait" | "immediate";
}

/** Mirrors api UpdateLiveStreamDTO. resolution/fps are fixed per plan and absent. */
export interface IUpdateLiveStreamRequest {
  id: string;
  playlistId?: string;
  videoId?: string;
  /** Primary playlist content is validated and saved with the stream settings. */
  primaryPlaylist?: ICreatePlaylistRequest;
  streamingPlatformsIds?: string[];
  /** @deprecated Send endOption. Kept for compatibility with older callers. */
  repeatOnEnd?: boolean;
  termsAgreedAt?: Date;
  contentAgreedAt?: Date;
  startOption?: LiveStreamStartOptionEnum;
  endOption?: LiveStreamEndOptionEnum;
  startDate?: Date;
  endDate?: Date;
  playbackOrder?: LiveStreamPlaybackOrderEnum;
  secondaryVideoId?: string | null;
  secondaryVideoVolume?: number;
  videoWidth?: number;
  videoHeight?: number;
  videoLeft?: number;
  videoTop?: number;
  orientation?: LiveStreamOrientationEnum;
  videoBitrate?: number;
  videoFPS?: number;
  videoFadeInDuration?: number;
  videoFadeOutDuration?: number;
  audioMixingEnabled?: boolean;
  audioMixingDuration?: number;
  crfMode?: boolean;
  audioBitrate?: number;
  liveStreamScheduledShows?: ILiveStreamScheduledShow[];
  jinglePlaylistId?: string | null;
  jingleVideoId?: string | null;
  jingleEvery?: number;
  jingleEveryType?: LiveStreamJingleEveryTypeEnum;
  jinglePlaybackOrder?: LiveStreamPlaybackOrderEnum;
}

export interface ICreateVideoRequest {
  name: string;
  export: boolean;
  fadeBetweenBackgrounds: boolean;
  backgrounds: {
    visualMediaId: string;
    order: number;
    startSecond: number;
    endSecond: number;
  }[];
  audios: { audioId: string; order: number }[];
  overlays?: Omit<
    IOverlay,
    "id" | "visualMedia" | "url" | "isSelected" | "videoId" | "playlistId"
  >[];
}

export interface IUpdateVideoRequest extends ICreateVideoRequest {
  id: string;
}

export interface ICreatePlaylistRequest {
  name: string;
  playlistVisualMedias: { visualMediaId: string; order: number }[];
  overlays?: Omit<
    IOverlay,
    "id" | "visualMedia" | "url" | "isSelected" | "videoId" | "playlistId"
  >[];
}

export interface IUpdatePlaylistRequest extends ICreatePlaylistRequest {
  id: string;
}

export interface IOverlaySelectable extends IOverlay {
  isSelected: boolean;
}

export interface IScheduledShow {
  id: string;
  name: string;
  /** Minutes after local midnight (0–1439); the show repeats daily (decision #13). */
  startMinuteOfDay: number;
  /** IANA time zone of startMinuteOfDay, e.g. "Europe/Berlin". */
  timezone: string;
  items: IStreamSelectedItem[];
  backgroundId?: string;
  playlistId?: string;
  videoId?: string;
  switchMode: "wait" | "immediate";
}

export enum IStreamPlaylistType {
  primary = "primary",
  scheduled = "scheduled",
  jingle = "jingle",
  secondary = "secondary",
}
